import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useState,
} from "react";
import Sider from "common/Sider/Sider";
import { Header } from "common/Home/Header/Header";
import { PageWrapperContext } from "common/Home/PageWrapper.context";
import { HeaderSearch } from "types";
import useMedia from "utils/hooks/useMedia";
import {
  HIDE_INTERCOM_LAUNCHER_BREAKPOINT,
  MAIN_CONTENT_ID,
  MOBILE_FILTERS,
  MOBILE_SEARCH,
} from "common/constants";
import styled from "styled-components";
import Banners from "common/Home/Banners/Banners";
import { useBottomNavigator } from "utils/hooks/misc";
import useMeasure from "utils/hooks/useMeasure";
import { AuthUser, HeaderFilters } from "@shared/types";
import BottomNavigator from "common/Home/BottomNavigator";

interface PageWrapperProps {
  user: AuthUser;
}

const PageWrapper: FC<PropsWithChildren<PageWrapperProps>> = ({
  children,
  user,
}) => {
  const [measureBannersRef, bannersMeasurements] = useMeasure<HTMLDivElement>();
  const [measureHeaderRef, headerMeasurements] = useMeasure<HTMLDivElement>();
  const [filters, setFilters] = useState<HeaderFilters | null>(null);
  const [search, setSearch] = useState<HeaderSearch>({
    show: false,
    value: "",
  });
  const [hideMobileFilters, setHideMobileFilters] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileOpen] = useState(false);
  const [titleExtraComponent, setTitleExtraComponent] = useState<ReactNode>();
  const [measurePageRef, pageSize] = useMeasure<HTMLDivElement>();

  const useMobileFilters = useMedia(
    [`(max-width: ${MOBILE_FILTERS})`],
    [true],
    false
  );
  const useMobileSearch = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const intercomLauncherVisible = useMedia(
    [`(max-width: ${HIDE_INTERCOM_LAUNCHER_BREAKPOINT})`],
    [false],
    true
  );
  const bottomNavigatorData = useBottomNavigator();
  const isBottomNavigator = bottomNavigatorData.baseShouldDisplay;
  const hideBottomSpacer = !intercomLauncherVisible;

  const setShowSearch = (show: boolean) => {
    setSearch({ ...search, show });
  };

  const setSearchValue = (value: string) => {
    setSearch({ ...search, value });
  };

  const pageContentSize = pageSize.height
    ? {
        ...pageSize,
        height:
          pageSize.height -
          headerMeasurements.height -
          (bottomNavigatorData.baseShouldDisplay
            ? 0
            : bannersMeasurements.height) -
          bottomNavigatorData.height,
      }
    : pageSize;

  const contextValues = useMemo(() => {
    return {
      setFilters,
      filters,
      setShowSearch,
      setSearchValue,
      setHideMobileFilters,
      mobileFiltersOpen,
      setMobileFiltersOpen,
      search,
      useMobileFilters,
      useMobileSearch,
      distanceTop: bannersMeasurements.height,
      pageSize: pageContentSize,
      titleExtraComponent,
      setTitleExtraComponent,
    };
  }, [
    filters,
    mobileFiltersOpen,
    search,
    useMobileFilters,
    useMobileSearch,
    bannersMeasurements.height,
    pageContentSize,
    titleExtraComponent,
  ]);

  const banners = (
    <div ref={measureBannersRef}>
      <Banners user={user} />
    </div>
  );

  return (
    <PageWrapperContext.Provider value={contextValues}>
      <StyledLayout ref={measurePageRef}>
        {!isBottomNavigator && banners}
        <ContentAndHeaderWrapper>
          <Sider
            mobileMenuOpen={isMobileMenuOpen}
            setMobileMenuOpen={setIsMobileOpen}
          />
          <div style={{ width: "100%", overflow: "hidden" }}>
            <div ref={measureHeaderRef}>
              <Header
                toggleMobileMenu={() => setIsMobileOpen(!isMobileMenuOpen)}
                user={user}
                hideMobileFilters={hideMobileFilters}
                banners={isBottomNavigator ? banners : undefined}
              />
            </div>
            <ContentWrapper
              id={MAIN_CONTENT_ID}
              style={{ height: pageContentSize.height }}
            >
              <ContentInnerWrapper
                $bottomNavigator={isBottomNavigator}
                $minHeight={pageContentSize.height}
              >
                {children}
              </ContentInnerWrapper>
              {!hideBottomSpacer && <BottomSpacer />}
            </ContentWrapper>
          </div>
        </ContentAndHeaderWrapper>
        {isBottomNavigator && <BottomNavigator setMenuOpen={setIsMobileOpen} />}
      </StyledLayout>
    </PageWrapperContext.Provider>
  );
};

export default PageWrapper;

const BOTTOM_SPACER_HEIGHT = 90;

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  overflow: hidden;
  background: ${(p) => p.theme.colors.layoutBgColor};
`;
const ContentWrapper = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;
const ContentInnerWrapper = styled.div<{
  $bottomNavigator?: boolean;
  $minHeight: number;
}>`
  width: 100%;
  ${(p) =>
    p.$bottomNavigator
      ? `
  height: 100%;
  display: flex;
  flex-direction: column;
  `
      : `
  height: fit-content;
  min-height: ${p.$minHeight}px
  `};
`;
const ContentAndHeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  min-height: 0;
`;
const BottomSpacer = styled.div`
  width: 100%;
  height: ${BOTTOM_SPACER_HEIGHT}px;
`;
